import {
  ArrowLeftOutline,
  Badge,
  CheckOutline,
  ChevronUpOutline,
  CreateBenefitCategoryRequest,
  DocumentOutline,
  ExternalLinkOutline,
  FaqOutline,
  GalleryOutline,
  TickSquareOutline,
  UserOutline,
  useToast,
} from '@aslanpay/portal-common-lib';
import { Button, Flex, IconButton, Text, useDisclosure } from '@chakra-ui/react';
import FormSection from 'app/components/UI/FormSection/FormSection';
import useAuthContext from 'app/hooks/useAuthContext';
import useBenefits from 'app/hooks/useBenefits';
import { FormProvider, Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import AddCategoryModal from 'app/components/Modals/AddCategoryModal';
import AddBenefitNoCategoriesAssignedModal from 'app/components/Modals/AddBenefitNoCategoriesAssignedModal/AddBenefitNoCategoriesAssignedModal';
import useBenefitCategories from 'app/hooks/useBenefitCategories';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import AdditionalLinksFields from './Fields/AdditionalLinksFields';
import BasicInformationFields from './Fields/BasicInformationFields';
import DocumentsFields from './Fields/DocumentsFields/DocumentsFields';
import EligibilityFields from './Fields/EligibilityFields';
import FaqFields from './Fields/FaqFields';
import MediaFields from './Fields/MediaFields';
import WhatsIncludedFields from './Fields/WhatsIncludedFields';
import {
  addBenefitsSchema,
  AddEditBenefitFormFields,
  AddEditBenefitFormProps,
  DEFAULT_ADD_BENEFIT_FORM_DATA,
} from './AddEditBenefitForm.types';

import convertBenefitToFormData from './AddEditBenefitForm.function';
import { CategoryDropdownOption } from './Fields/BasicInformationFields/BasicInformationFields.types';

function AddEditBenefitForm({
  saveDraftBenefit,
  publishBenefit,
  handleBack,
  benefit,
  isSaving = false,
}: AddEditBenefitFormProps) {
  const isEditMode = !!benefit;

  const [showScrollTop, setShowScrollTop] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = formRef.current?.closest('[data-scroll-container]') ?? window;
    const getScrollTop = () =>
      container === window ? window.scrollY : (container as Element).scrollTop;

    const handleScroll = () => setShowScrollTop(getScrollTop() > 300);
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    const container = formRef.current?.closest('[data-scroll-container]') ?? window;
    container.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const { company } = useAuthContext();
  const toast = useToast();

  const {
    isOpen: isAddCategoryModalOpen,
    onOpen: openAddCategoryModal,
    onClose: closeAddCategoryModal,
  } = useDisclosure();

  const [addCategoryData, setAddCategoryData] = useState<CreateBenefitCategoryRequest[]>([]);

  const {
    isOpen: isNoCategoriesModalOpen,
    onOpen: openNoCategoriesModal,
    onClose: closeNoCategoriesModal,
  } = useDisclosure();

  const { data: benefitCategories } = useBenefitCategories.useFetchBenefitCategories(
    company?.id,
    {
      page: 0,
      size: 999999,
    },
    !!company?.id
  );

  const [optionsCategory, setOptionsCategory] = useState<CategoryDropdownOption[]>([]);

  // Sync from the API only when there are no pending temp categories, so a query
  // reload mid-creation doesn't overwrite the temp entries
  useEffect(() => {
    if (addCategoryData.length === 0) {
      setOptionsCategory(
        (benefitCategories?.list ?? [])
          .map((cat) => ({ value: cat.id, text: cat.name }))
          .sort((a, b) => a.text.localeCompare(b.text))
      );
    }
  }, [benefitCategories, addCategoryData.length]);

  const { mutateAsync: createCategoryAsync, isPending } =
    useBenefitCategories.useCreateBenefitCategory(company?.id);

  const { uploadFile } = useBenefits.useUploadBenefitAsset(company?.id);

  const methods = useForm<AddEditBenefitFormFields>({
    mode: 'onChange',
    defaultValues: benefit ? convertBenefitToFormData(benefit) : DEFAULT_ADD_BENEFIT_FORM_DATA,
    resolver: yupResolver(addBenefitsSchema) as Resolver<AddEditBenefitFormFields>,
  });

  const {
    handleSubmit,
    formState: { isValid, isDirty, errors },
  } = methods;

  const { invalid: isInvalidBenefitName, isDirty: isDirtyBenefitName } =
    methods.getFieldState('benefitName');

  const isBenefitNameValid = !isInvalidBenefitName && (isDirtyBenefitName || isEditMode);
  const hasErrors = Object.keys(errors).length > 0;

  const watchDescription = methods.watch('description');
  const watchLogoUrl = methods.watch('logoUrl');
  const isPublished = benefit?.status === 'PUBLISHED';
  const isReadyToPublish =
    isValid && !hasErrors && isBenefitNameValid && !!watchDescription?.trim() && !!watchLogoUrl;

  const handleSaveTemporaryCategory = useCallback(
    (category: CreateBenefitCategoryRequest) => {
      const normalizedName = category.name.trim();
      const normalizedCategory: CreateBenefitCategoryRequest = {
        ...category,
        name: normalizedName,
      };
      const isDuplicateCategory = optionsCategory.some(
        (c) => c.text.trim().toLocaleLowerCase() === normalizedName.toLocaleLowerCase()
      );

      if (isDuplicateCategory) {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Error',
          description: `A category with this name already exists. Please choose a different name.`,
        });
        return;
      }
      setAddCategoryData((prev) => [...prev, normalizedCategory]);
      setOptionsCategory((prev) =>
        [...prev, { value: normalizedName, text: normalizedName, isNew: true }].sort((a, b) =>
          a.text.localeCompare(b.text)
        )
      );

      methods.setValue(
        'categoryIds',
        [...(methods.getValues('categoryIds') ?? []), normalizedName],
        {
          shouldDirty: true,
        }
      );

      closeAddCategoryModal();
    },
    [optionsCategory, toast, methods, closeAddCategoryModal]
  );

  const saveTemporaryCategories = useCallback(
    async (info: AddEditBenefitFormFields) => {
      const nameToIdMap: Record<string, string> = {};
      const failedNames: string[] = [];

      await Promise.all(
        addCategoryData.map(async (category) => {
          try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const data: any = await createCategoryAsync(category);
            nameToIdMap[category.name] = data.id;
            toast({
              position: 'top-right',
              status: 'success',
              title: 'Success',
              description: `Category "${category.name}" created successfully.`,
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } catch (error: any) {
            failedNames.push(category.name);
            const errorMessage = error?.response?.data?.message;
            const customMessage =
              typeof errorMessage === 'string' && errorMessage.includes('already exists in company')
                ? `A category with this name already exists for this company. Please choose a different name.`
                : undefined;
            toast({
              position: 'top-right',
              status: 'error',
              title: 'Error',
              description:
                customMessage || `Failed to create category "${category.name}". Please try again.`,
            });
          }
        })
      );

      // Replace temp placeholder IDs with real IDs for succeeded ones,
      // then strip out any that are still a temp name (i.e. failed — never got a real ID)
      const finalCategoryIds = (info.categoryIds ?? [])
        .map((id) => nameToIdMap[id] ?? id)
        .filter((id) => !failedNames.includes(id));

      // Single batch: remove all isNew entries, add back only the ones that succeeded
      setOptionsCategory((prev) => {
        const withoutNew = prev.filter((o) => !o.isNew);
        const realEntries = Object.entries(nameToIdMap).map(([name, id]) => ({
          value: id,
          text: name,
        }));
        return [...withoutNew, ...realEntries].sort((a, b) => a.text.localeCompare(b.text));
      });
      setAddCategoryData([]);
      methods.setValue('categoryIds', finalCategoryIds, { shouldDirty: true });

      return {
        ...info,
        categoryIds: finalCategoryIds,
      };
    },
    [addCategoryData, createCategoryAsync, toast, methods]
  );

  const handleSaveDraftBenefit = async (info: AddEditBenefitFormFields) => {
    const infoWithTemporaryCategories = await saveTemporaryCategories(info);
    return saveDraftBenefit(infoWithTemporaryCategories);
  };

  const handleSavePublishBenefit = async (info: AddEditBenefitFormFields) => {
    const infoWithTemporaryCategories = await saveTemporaryCategories(info);
    return publishBenefit(infoWithTemporaryCategories);
  };

  // still submit as a draft if validation fails
  const handleSaveAsDraft = handleSubmit(
    async (info) => {
      return handleSaveDraftBenefit(info);
    },
    async () => {
      return handleSaveDraftBenefit(methods.getValues());
    }
  );

  // if no categories are assigned, it will open a confirmation modal, if there are values, publish directly
  const handleSubmitToPublish = handleSubmit(async (data) => {
    if (!data.description || data.description.trim().length < 3) {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Error',
        description: 'Description is mandatory for publishing.',
      });
      return;
    }

    if (data.categoryIds?.length === 0) {
      openNoCategoriesModal();
      return;
    }

    await handleSavePublishBenefit(data);
  });

  // returning from the modal, if there are selected categories, assign those and publish, if not, publish with no categories
  const handleSaveOnNoCategoriesAssignedModal = async (selectedCategories: string[]) => {
    const formData = methods.getValues();

    const dataWithSelectedCategories: AddEditBenefitFormFields = {
      ...formData,
      categoryIds: [...(formData?.categoryIds ?? []), ...selectedCategories],
    };

    await handleSavePublishBenefit(dataWithSelectedCategories);

    // after publish, we can close the modal
    closeNoCategoriesModal();
  };

  const renderFormHeader = useMemo(() => {
    if (isEditMode && benefit) {
      return (
        <>
          <Text as="h2" textStyle="h2" color="brand.darkPurple">
            Edit benefit
          </Text>

          <Flex direction="row" gap="10px" justifyContent="center" alignItems="center">
            <Text as="p" textStyle="bodyRegular" color="brand.darkPurple">
              {`Update the details for "${benefit.name}".`}
            </Text>
            <Badge
              status={benefit.status === 'PUBLISHED' ? 'active' : 'locked'}
              value={benefit.status === 'PUBLISHED' ? 'Published' : 'Draft'}
            />
          </Flex>
        </>
      );
    }

    return (
      <Text as="h2" textStyle="h2" color="brand.darkPurple">
        Add new benefit
      </Text>
    );
  }, [isEditMode, benefit]);

  const isDraftButtonDisabled = () => {
    if (isEditMode) {
      if (isPublished) return false;
      return !isDirty || !isBenefitNameValid;
    }
    return !isBenefitNameValid;
  };
  const draftButtonLabel = () => {
    if (isEditMode) {
      if (isPublished) return isDirty ? 'Update & Move to draft' : 'Move to draft';
      return 'Update & Save as draft';
    }
    return 'Save as draft';
  };
  return (
    <>
      <FormProvider {...methods}>
        <Flex ref={formRef} flexDir="column" gap="30px" h="full">
          <Flex justifyContent="space-between" alignItems="center">
            <Flex flexDir="column" gap="20px">
              <Button
                alignSelf="flex-start"
                size="sm"
                variant="link"
                leftIcon={<ArrowLeftOutline boxSize="20px" />}
                onClick={handleBack}
                padding="0"
              >
                Back
              </Button>
              {renderFormHeader}
            </Flex>
            <Flex gap="30px">
              <Button
                variant="outline"
                size="md"
                onClick={handleSaveAsDraft}
                isDisabled={isDraftButtonDisabled() || isSaving}
                isLoading={isSaving}
              >
                {draftButtonLabel()}
              </Button>
              <Button
                size="md"
                leftIcon={<CheckOutline boxSize="20px" />}
                onClick={handleSubmitToPublish}
                isDisabled={
                  isSaving ||
                  (isEditMode
                    ? (isPublished && !isDirty) || !isReadyToPublish
                    : hasErrors || !isReadyToPublish)
                }
                isLoading={isSaving}
              >
                {isEditMode && isDirty ? 'Update & Publish' : 'Publish'}
              </Button>
            </Flex>
          </Flex>

          <Flex flexDir="column" gap="20px" w="full">
            <FormSection
              icon={<DocumentOutline boxSize="20px" color="brand.mediumPurple" />}
              title="Basic information"
            >
              <BasicInformationFields
                onOpenAddCategoryModal={openAddCategoryModal}
                optionsCategory={optionsCategory}
              />
            </FormSection>

            <FormSection
              icon={<DocumentOutline boxSize="20px" color="brand.mediumPurple" />}
              title="Documents"
              description="Upload PDF documents employees can access for this benefit."
            >
              <DocumentsFields companyId={company?.id ?? ''} />
            </FormSection>

            <FormSection
              icon={<ExternalLinkOutline boxSize="20px" color="brand.mediumPurple" />}
              title="Additional links"
              description="Add additional links employees can access for this benefit."
            >
              <AdditionalLinksFields />
            </FormSection>

            <FormSection
              icon={<TickSquareOutline boxSize="20px" color="brand.mediumPurple" />}
              title="What's included"
              description="Add the key highlights employees will see."
            >
              <WhatsIncludedFields />
            </FormSection>

            <FormSection
              icon={<UserOutline boxSize="20px" color="brand.mediumPurple" />}
              title="Does this benefit apply to me?"
              description="Add eligibility criteria so employees know if this benefit applies to them. (Optional)"
            >
              <EligibilityFields />
            </FormSection>

            <FormSection
              icon={<GalleryOutline boxSize="20px" color="brand.mediumPurple" />}
              title="Media"
            >
              <MediaFields uploadFile={uploadFile} />
            </FormSection>

            <FormSection
              icon={<FaqOutline boxSize="20px" color="brand.mediumPurple" />}
              title="FAQs"
            >
              <FaqFields />
            </FormSection>
          </Flex>
        </Flex>
      </FormProvider>

      {showScrollTop && (
        <IconButton
          aria-label="Scroll to top"
          icon={<ChevronUpOutline boxSize="24px" />}
          position="fixed"
          bottom="40px"
          right="40px"
          zIndex={10}
          borderRadius="full"
          bg="brand.purple"
          color="white"
          boxSize="48px"
          _hover={{ opacity: 0.85 }}
          onClick={scrollToTop}
        />
      )}

      <AddCategoryModal
        isOpen={isAddCategoryModalOpen}
        onClose={closeAddCategoryModal}
        onSave={handleSaveTemporaryCategory}
        isSaving={isPending}
      />

      <AddBenefitNoCategoriesAssignedModal
        isOpen={isNoCategoriesModalOpen}
        onClose={closeNoCategoriesModal}
        options={optionsCategory}
        onSave={handleSaveOnNoCategoriesAssignedModal}
      />
    </>
  );
}

export default AddEditBenefitForm;
